import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import TableLayout from "./TableLayout";
import useLocale from "../locales";

const PlTable = ({
  showTable,
  activeTab,
  historyData,
  calcDone,
  shouldGenerateReport,
  setShouldGenerateReport,
}) => {
  const { textData } = useLocale();
  const [amortizationData, setAmortizationData] = useState([]);
  const { loanAmount, timePeriod, intrestRate, perEmi } = useSelector(
    (state) => state.personalLoanCalculator
  );
  const calculatorState = useSelector((state) => state.personalLoanCalculator);

  useEffect(() => {
    if (historyData) {
      generateHistoryReport();
    }
  }, [showTable, loanAmount, timePeriod, intrestRate, perEmi, historyData]);

  useEffect(() => {
    if (shouldGenerateReport && calcDone) {
      generateAmortizationSchedule();
      setShouldGenerateReport(false);
    } else if (!calcDone) {
      setAmortizationData([]);
    }
    generateHistoryReport();
  }, [shouldGenerateReport, calcDone, historyData]);

  const generateHistoryReport = () => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return;
    }

    const plData = historyData[0];

    const principal = parseFloat(plData.totalInvestment);
    const rate = parseFloat(plData.interestRate) / 1200; // Convert annual rate to monthly
    const tenure = parseFloat(plData.tenure);

    if (!tenure) {
      return;
    }

    const totalMonths = tenure * 12;

    let emi;
    if (plData.perEmi) {
      emi = parseFloat(plData.perEmi);
    } else {
      emi =
        (principal * rate * Math.pow(1 + rate, totalMonths)) /
        (Math.pow(1 + rate, totalMonths) - 1);
    }

    const yearlyData = [];
    let remainingBalance = principal;
    let cumulativeInterestPaid = 0;
    let totalYears = Math.ceil(totalMonths / 12);

    for (let year = 1; year <= totalYears; year++) {
      const openingBalance = remainingBalance;
      let yearlyInterestPaid = 0;
      let yearlyPrincipalPaid = 0;
      let monthsInYear = 12;

      // For the last year, calculate remaining months
      if (year === totalYears) {
        monthsInYear = totalMonths - (year - 1) * 12;
      }

      for (let month = 1; month <= monthsInYear; month++) {
        const interestForMonth = remainingBalance * rate;
        const principalForMonth = emi - interestForMonth;

        yearlyInterestPaid += interestForMonth;
        yearlyPrincipalPaid += principalForMonth;
        remainingBalance -= principalForMonth;

        if (remainingBalance < 0) {
          remainingBalance = 0;
        }
      }

      cumulativeInterestPaid += yearlyInterestPaid;

      yearlyData.push({
        year,
        openingBalance,
        interestPaid: yearlyInterestPaid,
        principalPaid: yearlyPrincipalPaid,
        monthlyPayment: emi,
        totalRepayment: emi * monthsInYear,
        totalInterestPaid: cumulativeInterestPaid,
      });
    }

    setAmortizationData(yearlyData);
  };

  const generateAmortizationSchedule = () => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(intrestRate) / 1200;
    const totalMonths = parseFloat(timePeriod) * 12;
    const emi = parseFloat(perEmi);

    const yearlyData = [];
    let remainingBalance = principal;
    let cumulativeInterestPaid = 0;
    let totalYears = Math.ceil(totalMonths / 12);

    for (let year = 1; year <= totalYears; year++) {
      const openingBalance = remainingBalance;
      let yearlyInterestPaid = 0;
      let yearlyPrincipalPaid = 0;
      let monthsInYear = 12;

      // For the last year, calculate remaining months
      if (year === totalYears) {
        monthsInYear = totalMonths - (year - 1) * 12;
      }

      for (let month = 1; month <= monthsInYear; month++) {
        const interestForMonth = remainingBalance * rate;
        const principalForMonth = emi - interestForMonth;

        yearlyInterestPaid += interestForMonth;
        yearlyPrincipalPaid += principalForMonth;
        remainingBalance -= principalForMonth;

        // Prevent negative balance
        if (remainingBalance < 0) {
          remainingBalance = 0;
        }
      }

      cumulativeInterestPaid += yearlyInterestPaid;

      yearlyData.push({
        year,
        openingBalance,
        interestPaid: yearlyInterestPaid,
        principalPaid: yearlyPrincipalPaid,
        monthlyPayment: emi,
        totalRepayment: emi * monthsInYear,
        totalInterestPaid: cumulativeInterestPaid,
      });
    }

    setAmortizationData(yearlyData);
  };
  const columns = [
    { key: "year", label: textData.tableLabels.year, format: (value) => value },
    {
      key: "openingBalance",
      label: `${textData.tableLabels.openingBalance} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "interestPaid",
      label: `${textData.tableLabels.intrestPaid} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "principalPaid",
      label: `${textData.tableLabels.principlePaid} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "monthlyPayment",
      label: `${textData.tableLabels.monthlyPayment} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "totalRepayment",
      label: `${textData.tableLabels.totalrepayment} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "totalInterestPaid",
      label: `${textData.tableLabels.totalInterestPaid} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
  ];

  return (
    <TableLayout
      showTable={showTable}
      title={`${textData.filterDropDown.personalLoanCalc} ${textData.report}`}
      data={amortizationData}
      columns={columns}
      analyticsEventName="download_pl_report"
      reportTitle={textData.reportTitle.personalLoan}
      fileName="personal_loan_report"
    />
  );
};

export default PlTable;
